import { stripInlineReplyTagsForDisplay } from "../../../../src/utils/directive-tags.js";
import { stripThinkingTags } from "../format.ts";

const ENVELOPE_PREFIX = /^\[([^\]]+)\]\s*/;
const ENVELOPE_CHANNELS = [
  "WebChat",
  "WhatsApp",
  "Telegram",
  "Signal",
  "Slack",
  "Discord",
  "iMessage",
  "Teams",
  "Matrix",
  "Zalo",
  "Zalo Personal",
  "BlueBubbles",
];

const textCache = new WeakMap<object, string | null>();
const thinkingCache = new WeakMap<object, string | null>();

function looksLikeEnvelopeHeader(header: string): boolean {
  if (/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}Z\b/.test(header)) {
    return true;
  }
  if (/\d{4}-\d{2}-\d{2} \d{2}:\d{2}\b/.test(header)) {
    return true;
  }
  return ENVELOPE_CHANNELS.some((label) => header.startsWith(`${label} `));
}

export function stripEnvelope(text: string): string {
  const match = text.match(ENVELOPE_PREFIX);
  if (!match) {
    return text;
  }
  const header = match[1] ?? "";
  if (!looksLikeEnvelopeHeader(header)) {
    return text;
  }
  return text.slice(match[0].length);
}

/**
 * Strip an echoed timestamp envelope from an ASSISTANT message.
 *
 * Models occasionally copy the inbound envelope shape they were shown
 * (`[Wed 2026-10-14 14:37 UTC] — Welcome!`) and emit it as their own prefix.
 * Assistant rows are not envelope-stripped on the normal path, so the raw
 * stamp leaked into the bubble.
 *
 * Deliberately narrow: only a *timestamp-shaped* header qualifies, and only a
 * single following ` — ` is consumed. A legitimate reply that opens with a
 * bracketed phrase (`[note] …`) is left untouched.
 */
export function stripAssistantEnvelope(text: string): string {
  const stripped = stripEnvelope(text);
  if (stripped === text) {
    return text;
  }
  return stripped.replace(/^—\s*/, "");
}

function stripAssistantDisplayText(text: string): string {
  return stripInlineReplyTagsForDisplay(stripThinkingTags(stripAssistantEnvelope(text)));
}

export function extractAssistantErrorMessage(message: unknown): string | null {
  if (message == null) {
    return null;
  }
  const m = message as Record<string, unknown>;
  const role = typeof m.role === "string" ? m.role.toLowerCase() : "";
  if (role !== "assistant") {
    return null;
  }
  const errorMessage = typeof m.errorMessage === "string" ? m.errorMessage.trim() : "";
  if (errorMessage) {
    return errorMessage;
  }
  const stopReason = typeof m.stopReason === "string" ? m.stopReason.toLowerCase() : "";
  if (stopReason === "error" && typeof m.text === "string" && m.text.trim()) {
    return m.text.trim();
  }
  return null;
}

/** Error from the most recent assistant message only (ignore older failures). */
export function findLastAssistantChatError(messages: unknown[]): string | null {
  if (!Array.isArray(messages)) {
    return null;
  }
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i] as Record<string, unknown>;
    const role = typeof m.role === "string" ? m.role.toLowerCase() : "";
    if (role !== "assistant") {
      continue;
    }
    return extractAssistantErrorMessage(m);
  }
  return null;
}

export function extractText(message: unknown): string | null {
  const m = message as Record<string, unknown>;
  const role = typeof m.role === "string" ? m.role : "";
  const content = m.content;
  if (typeof content === "string") {
    const processed =
      role === "assistant" ? stripAssistantDisplayText(content) : stripEnvelope(content);
    return processed;
  }
  if (Array.isArray(content)) {
    const parts = content
      .map((p) => {
        const item = p as Record<string, unknown>;
        if (item.type === "text" && typeof item.text === "string") {
          return item.text;
        }
        return null;
      })
      .filter((v): v is string => typeof v === "string");
    if (parts.length > 0) {
      const joined = parts.join("\n");
      const processed =
        role === "assistant" ? stripAssistantDisplayText(joined) : stripEnvelope(joined);
      return processed;
    }
  }
  if (typeof m.text === "string") {
    const processed =
      role === "assistant" ? stripAssistantDisplayText(m.text) : stripEnvelope(m.text);
    return processed;
  }
  return null;
}

export function extractTextCached(message: unknown): string | null {
  if (!message || typeof message !== "object") {
    return extractText(message);
  }
  const obj = message;
  if (textCache.has(obj)) {
    return textCache.get(obj) ?? null;
  }
  const value = extractText(message);
  textCache.set(obj, value);
  return value;
}

export function extractThinking(message: unknown): string | null {
  const m = message as Record<string, unknown>;
  const content = m.content;
  const parts: string[] = [];
  if (Array.isArray(content)) {
    for (const p of content) {
      const item = p as Record<string, unknown>;
      const type = typeof item.type === "string" ? item.type.toLowerCase() : "";
      if (type !== "thinking" && type !== "reasoning") {
        continue;
      }
      const raw =
        typeof item.thinking === "string"
          ? item.thinking
          : typeof item.text === "string"
            ? item.text
            : typeof item.reasoning === "string"
              ? item.reasoning
              : "";
      const cleaned = raw.trim();
      if (cleaned) {
        parts.push(cleaned);
      }
    }
  }
  if (parts.length > 0) {
    return parts.join("\n");
  }

  // JAISIU-restore-thinking-on-chat-switch — the in-memory streaming
  // tail (see `chat-streaming-tail.ts:StreamingTranscriptTail`) carries
  // the live reasoning text on a top-level `reasoning` field rather
  // than inside `content[]`. That shape is what the live bubble paints
  // while a run is in flight, and it's the only persisted representation
  // available on a chat-tab switch or page refresh mid-run (the
  // per-session cache at `state.chatMessagesBySession` stores the same
  // object). Without this fallback, the historical bubble falls back
  // to the generic "Thinking…" placeholder even though the reasoning
  // content is in the message — the operator sees an empty transcript
  // until `chat.final` lands and the server projection rewrites the
  // row with `content[].thinking` parts.
  const topLevelReasoning =
    typeof m.reasoning === "string"
      ? m.reasoning
      : typeof m.reasoningText === "string"
        ? m.reasoningText
        : "";
  const trimmedTopLevel = topLevelReasoning.trim();
  if (trimmedTopLevel) {
    return trimmedTopLevel;
  }

  // Back-compat: older logs may still have <think> tags inside text blocks.
  const rawText = extractRawText(message);
  if (!rawText) {
    return null;
  }
  const matches = [
    ...rawText.matchAll(/<\s*think(?:ing)?\s*>([\s\S]*?)<\s*\/\s*think(?:ing)?\s*>/gi),
  ];
  const extracted = matches.map((match) => (match[1] ?? "").trim()).filter(Boolean);
  return extracted.length > 0 ? extracted.join("\n") : null;
}

export function extractThinkingCached(message: unknown): string | null {
  if (!message || typeof message !== "object") {
    return extractThinking(message);
  }
  const obj = message;
  if (thinkingCache.has(obj)) {
    return thinkingCache.get(obj) ?? null;
  }
  const value = extractThinking(message);
  thinkingCache.set(obj, value);
  return value;
}

export function extractRawText(message: unknown): string | null {
  const m = message as Record<string, unknown>;
  const content = m.content;
  if (typeof content === "string") {
    return content;
  }
  if (Array.isArray(content)) {
    const parts = content
      .map((p) => {
        const item = p as Record<string, unknown>;
        if (item.type === "text" && typeof item.text === "string") {
          return item.text;
        }
        return null;
      })
      .filter((v): v is string => typeof v === "string");
    if (parts.length > 0) {
      return parts.join("\n");
    }
  }
  if (typeof m.text === "string") {
    return m.text;
  }
  return null;
}

export function formatReasoningMarkdown(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) {
    return "";
  }
  const lines = trimmed
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => `_${line}_`);
  return lines.length ? ["_Reasoning:_", ...lines].join("\n") : "";
}
